import React, { useState, useEffect } from 'react';
import { Modal, RadioGroup, FormControlLabel, Radio, Button, Box, Autocomplete, Pagination, Typography, TextField, Checkbox } from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../../config';

const LinkCode = (props) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const procList = props.procList.sort((a, b) => {
        if (a.procCode_description < b.procCode_description) {
            return -1;
        }
        if (a.procCode_description > b.procCode_description) {
            return 1;
        }
        return 0;
    });
    const [selectedProcCode, setSelectedProcCode] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [selectedPayer, setSelectedPayer] = useState(null);
    const [selectedOffice, setSelectedOffice] = useState(null);
    const [taskRate, setTaskRate] = useState(null);
    const [selectedDeliveryMode, setSelectedDeliveryMode] = useState(null);
    const [allowEarlyStart, setAllowEarlyStart] = useState(false);
    const [openUnlinkModal, setOpenUnlinkModal] = useState(false);

    useEffect(() => {
        const selectedProcData = props.procedureData.find((procData) => procData.procCode_id === props.originalSelectedProcCode?.procCode_id);
        setSelectedProcCode(props.originalSelectedProcCode || null);
        setSelectedProvider(selectedProcData?.providerIdentifier || null);
        setSelectedPayer(selectedProcData?.payerIdentifier || null);
        setSelectedOffice(selectedProcData?.officeIdentifier || null);
        setTaskRate(selectedProcData?.taskRate || null);
        setSelectedDeliveryMode(selectedProcData?.deliveryMode || null);
        setAllowEarlyStart(!!selectedProcData?.allowEarlyStart);
    }, [props.open]);

    const handleClose = async () => {
        props.setOpen(false);
        setOpenUnlinkModal(false);
        setSelectedProcCode(null);
        setSelectedProvider(null);
        setSelectedPayer(null);
        setSelectedOffice(null);
        setTaskRate(null);
        setSelectedDeliveryMode(null);
        setAllowEarlyStart(false);
    };

    const handleLink = async () => {
        await axios.post(APIURL + "/procedureCodes/handleMappingForTaskProcCode", {
            dropdown_id: props.taskType.id.toString(),
            procedure_code_id: selectedProcCode?.procCode_id.toString(),
            comp_id: props.comp_id,
            taskRate: taskRate,
            officeIdentifier: selectedOffice,
            payerIdentifier: selectedPayer,
            providerIdentifier: selectedProvider,
            deliveryMode: selectedDeliveryMode,
            allowEarlyStart: allowEarlyStart ? 1 : 0,
        });

        props.getProcedureData();
        props.onProcCodeSaved(props.taskType.id);

        handleClose();
    };

    const handleUnLink = async () => {
        await axios.put(APIURL + "/procedureCodes/handleMappingForTaskProcCode", {
            taskProcCode_id: props.procedureData.find((procData) => procData.procCode_id === selectedProcCode?.procCode_id)?.taskProcCode_id,
            deliveryMode: selectedDeliveryMode,
        });

        props.getProcedureData();
        props.onProcCodeSaved(props.taskType.id);

        handleClose();
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    // Calculate the items to display based on currentPage and itemsPerPage
    const paginatedItems = props.procList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div>
            <Modal enforceFocus={false}
                open={props.open} // Use props.open if provided, otherwise use local state
                onClose={handleClose}
                aria-labelledby="link-code-modal"
                aria-describedby="link-code-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        overflowY: 'auto'
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="h5" id="link-code-modal">
                                Link Code
                            </Typography>
                            <Typography variant="body1" id="link-code-modal-description">
                                Procedure Code with Task type - {props.taskType?.title}
                            </Typography>

                            <Autocomplete
                                id="ProcCode-select"
                                value={selectedProcCode}
                                options={procList}
                                getOptionLabel={(option) => option.procProcCode + (option.procModifierCode ? ":" + option.procModifierCode : "") + (option.procCode_description ? ` (${option.procCode_description})` : "")}
                                onChange={(event, value) => setSelectedProcCode(value)}
                                renderInput={(params) => <TextField {...params} label="Select Procedure Code" />}
                                fullWidth
                                size="small"
                            />

                            <Autocomplete
                                id="DeliveryMode-select"
                                value={selectedDeliveryMode}
                                options={['I', 'G']}
                                onChange={(event, value) => setSelectedDeliveryMode(value)}
                                renderInput={(params) => <TextField {...params} label="Delivery Mode" />}
                                fullWidth
                                size="small"
                            />

                            <TextField
                                variant="outlined"
                                label={`Enter Procedure Rate`}
                                type='number'
                                size='small'
                                value={taskRate}
                                onChange={(e) => setTaskRate(e.target.value)}
                                fullWidth
                            />

                            <Autocomplete
                                id="Provider-select"
                                value={props.providerList.find(prov => prov.prov_iden_id === selectedProvider)}
                                options={props.providerList}
                                getOptionLabel={(option) => option.prov_name}
                                onChange={(event, value) => setSelectedProvider(value.prov_iden_id)}
                                renderInput={(params) => <TextField {...params} label="Select Contract" />}
                                fullWidth
                                size="small"
                            />

                            <Autocomplete
                                id="Payer-select"
                                disabled={
                                    props.providerList.find(prov => prov.prov_iden_id === selectedProvider)?.prov_iden === '999999' &&
                                    props.providerList.find(prov => prov.prov_iden_id === selectedProvider)?.prov_name === "Contract for Private Payers"
                                }
                                value={props.payerList.find(payer => payer.payer_id === selectedPayer)}
                                options={props.payerList}
                                getOptionLabel={(option) => option.payer_name}
                                onChange={(event, value) => setSelectedPayer(value.payer_id)}
                                renderInput={(params) => <TextField {...params} label="Select Payer" />}
                                fullWidth
                                size="small"
                            />

                            <Autocomplete
                                id="Office-select"
                                value={props.officeList.find(office => office.oloc_id === selectedOffice)}
                                options={props.officeList}
                                getOptionLabel={(option) => option.oloc_addr_line1}
                                onChange={(event, value) => setSelectedOffice(value.oloc_id)}
                                renderInput={(params) => <TextField {...params} label="Select Office" />}
                                fullWidth
                                size="small"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allowEarlyStart}
                                        onChange={(e) => setAllowEarlyStart(e.target.checked)}
                                    />
                                }
                                label="Can start before its start date"
                            />

                            {/* <RadioGroup
                                aria-label="procedure-code"
                                name="procedure-code"
                                value={props.selectedProcCodes ? props.selectedProcCodes[props.taskType.id] : ''}
                                onChange={(e) => props.setSelectedProcCodes({ ...props.selectedProcCodes, [props.taskType.id]: e.target.value })}
                            >
                                {paginatedItems.map((proc) => (
                                    <FormControlLabel
                                        key={proc.procCode_id}
                                        value={proc.procCode_id}
                                        control={<Radio />}
                                        label={proc.procCode_description}
                                    />
                                ))}
                            </RadioGroup> */}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
                            {/* <Pagination
                                count={Math.ceil(props.procList.length / itemsPerPage)}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                            /> */}
                            <Button
                                variant="contained"
                                onClick={handleLink}
                                sx={{ mt: 2 }}
                                disabled={!selectedProcCode}
                            >
                                Link
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => setOpenUnlinkModal(true)}
                                sx={{ mt: 2 }}
                                disabled={!props.originalSelectedProcCode}
                            >
                                Unlink
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Modal enforceFocus={false}
                open={openUnlinkModal}
                onClose={() => setOpenUnlinkModal(false)}
                aria-labelledby="unlink-code-modal"
                aria-describedby="unlink-code-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        height: '30vh',
                        overflowY: 'auto',
                        zIndex: 2000
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                        <Box>
                            <Typography variant="h5" id="link-code-modal" sx={{ mb: 5, textAlign: 'center' }}>
                                Confirm Unlink Code
                            </Typography>
                            <Typography variant="h6" id="link-code-modal-description" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                                Unlinking the code can not be reversed. You may loose existing data and information with prior tasks FOREVER. Do you still want to continue?
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleUnLink}
                                sx={{ mt: 2 }}
                            >
                                Yes
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => setOpenUnlinkModal(false)}
                                sx={{ mt: 2 }}
                            >
                                No
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default LinkCode;
